import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Plus, Inbox, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WatchLaneModal } from "@/components/dispatcher/WatchLaneModal";
import { WatchesList } from "@/components/dispatcher/WatchesList";
import { DispatcherInbox } from "@/components/dispatcher/DispatcherInbox";
import { useDispatcherWatches } from "@/hooks/useDispatcherWatches";

export default function DispatcherPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const { watches } = useDispatcherWatches();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") === "watches" ? "watches" : "inbox";
  const watchFilter = searchParams.get("watch");

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "inbox") next.delete("tab"); else next.set("tab", value);
    if (value !== "inbox") next.delete("watch");
    setSearchParams(next, { replace: true });
  };

  const setWatchFilter = useCallback((id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("watch", id); else next.delete("watch");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleViewAlerts = useCallback((watchId: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    next.set("watch", watchId);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-brand-teal" />
            ATLAS Dispatcher
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            ATLAS watches your lanes 24/7 and surfaces the loads worth your call.
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)} className="bg-brand-teal hover:bg-brand-teal/90 text-white gap-2">
          <Plus className="h-4 w-4" />
          Watch a Lane
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="inbox" className="gap-2">
            <Inbox className="h-4 w-4" />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="watches" className="gap-2">
            <Settings className="h-4 w-4" />
            Watches ({watches.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-4">
          <DispatcherInbox watchFilter={watchFilter} onWatchFilterChange={setWatchFilter} />
        </TabsContent>
        <TabsContent value="watches" className="mt-4">
          <WatchesList onViewAlerts={handleViewAlerts} />
        </TabsContent>
      </Tabs>

      <WatchLaneModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
